import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { Doctor } from '@/models/domain/Doctor'
import { Product } from '@/models/domain/Product'
import { Patient } from '@/models/domain/Patient'
import { User } from '@/models/domain/User'
import { Unit } from '@/models/domain/Unit'
import { Location } from '@/models/domain/Location'
import { StartupData } from '@/models/auxiliary/StartupData'
import { HealthPlan } from '@/models/domain/HealthPlan'
import { HealthCarrier } from '@/models/domain/HealthCarrier'
import { Slot } from '@/models/domain/Slot'

export type FlowType = 'doctor' | 'product'

export interface ScheduleStore {
  schedulingId?: string
  user?: User
  product?: Product
  doctor?: Doctor
  healthPlan?: HealthPlan
  healthCarrier?: HealthCarrier
  patient?: Patient
  unit?: Unit
  location?: Location
  flowType?: FlowType
  slot?: Slot
  hasConfirmedSchedule?: boolean
  flowStart?: boolean
  appStarted?: boolean

  documentIds: number[] | null
  setStartupData(data: StartupData): void
  setProduct(product: Product | undefined): void
  setDoctor(doctor: Doctor | undefined): void
  setPatient(data: Partial<Patient>): void
  setScheduleData(storeKey: keyof ScheduleStore, data: any): void
  setFlowType(flowType: FlowType): void
  setData(data: Partial<ScheduleStore>): void
  setDocumentIds(ids: number[] | null): void
  reset: () => void
}
type InitialStateStore = Omit<
  ScheduleStore,
  | 'setStartupData'
  | 'setProduct'
  | 'setFlowType'
  | 'setDoctor'
  | 'setPatient'
  | 'setScheduleData'
  | 'setData'
  | 'reset'
  | 'setDocumentIds'
>

const initialState: InitialStateStore = {
  doctor: undefined,
  product: undefined,
  flowType: undefined,
  patient: undefined,
  healthCarrier: undefined,
  healthPlan: undefined,
  location: undefined,
  schedulingId: undefined,
  unit: undefined,
  slot: undefined,
  user: undefined,
  flowStart: false,
  hasConfirmedSchedule: false,
  documentIds: null
}

export const useScheduleStore = create<ScheduleStore>()(
  devtools(
    (set) => ({
      ...initialState,
      flowType: 'product',
      appStarted: false,
      setStartupData(data: StartupData) {
        const product = data.params.products && data.params.products[0]
        const healthCarrier = data?.params?.healthCarrier
        const healthPlan = data?.params?.healthPlan
        const location =
          data.params.location &&
          new Location().convertFromStartup(data.params.location)

        set(
          {
            ...data.params,
            flowType: data.params?.doctor?.id ? 'doctor' : 'product',
            schedulingId: data.schedulingId,
            user: data.user,
            product,
            patient: data.patient,
            healthCarrier: healthCarrier
              ? new HealthCarrier(healthCarrier)
              : undefined,
            healthPlan: healthPlan ? new HealthPlan(healthPlan) : undefined,
            location,
            appStarted: true
          },
          false,
          'setStartupData'
        )
      },
      setProduct(product?: Product) {
        set({ product }, false, 'setProduct')
      },
      setFlowType(flowType: FlowType) {
        set({ flowType }, false, 'setFlowType')
      },
      setDoctor(doctor: Doctor) {
        set({ doctor }, false, 'setDoctor')
      },
      setPatient(patient: Patient) {
        set(
          (state) => ({
            patient: { ...state.patient, ...patient }
          }),
          false,
          'setPatient'
        )
      },
      setScheduleData(storeKey, data) {
        set({ [storeKey]: data }, false, `setScheduleData(${storeKey})`)
      },
      setData(data: Partial<ScheduleStore>) {
        set(data, false, 'setData')
      },
      setDocumentIds(ids) {
        set({ documentIds: ids })
      },
      reset: () => {
        set(initialState)
      }
    }),
    {
      name: 'useScheduleStore'
    }
  )
)
